import React, { forwardRef, ReactNode } from "react";
import { TFunction } from "i18next";
import {
  Box,
  Text,
  useColorMode,
  Heading,
  Flex,
  useBreakpointValue,
} from "@chakra-ui/react";
import ChapterNavigation from "./ChapterNavigation";

interface BookContentProps {
  children?: ReactNode;
  chapter?: string;
  text?: string;
  onClick?: () => void;
  prefersReducedMotion?: boolean;
  isMobile?: boolean;
  t: TFunction;
  fontSize: {
    base: string;
    lg: string;
  };
  headingFontSize: {
    base: string;
  };
  currentChapterIndex: number;
  totalContentItems: number;
  customContent?: ReactNode;
  handlePreviousChapter: () => void;
  handleNextChapter: () => void;
  onOpenTableOfContents: () => void;
  showNavigation?: boolean;
  isNavigating?: boolean;
  navigationVariant?: "default" | "fullscreen";
  language?: string;
  showBottomMargin?: boolean;
}

const BookContent = forwardRef<HTMLDivElement, BookContentProps>(
  (
    {
      children,
      chapter,
      text,
      onClick,
      prefersReducedMotion,
      isMobile,
      t,
      fontSize,
      headingFontSize,
      currentChapterIndex,
      totalContentItems,
      customContent,
      handlePreviousChapter,
      handleNextChapter,
      onOpenTableOfContents,
      showNavigation = true,
      isNavigating = false,
      navigationVariant = "default",
      language = "en",
      showBottomMargin = true,
    },
    ref
  ) => {
    const { colorMode } = useColorMode();
    const adaptiveMobile =
      useBreakpointValue({ base: true, md: false }) || isMobile;

    // Generate dynamic navigation hint based on context
    const getNavigationHint = () => {
      if (currentChapterIndex === totalContentItems - 1) {
        // Last page - keep this special case
        return t("readBook.lastPage", "Last page");
      } else {
        // For all other pages, show page numbers instead of instructions
        return t("readBook.pageCounter", "Page {{current}} of {{total}}", {
          current: currentChapterIndex + 1,
          total: totalContentItems,
        });
      }
    };

    // Next button text changes for last chapter
    const nextButtonText =
      currentChapterIndex === totalContentItems - 2
        ? t("readBook.lastPage", "Last page")
        : t("readBook.nextChapter", "Next");

    return (
      <Box id="chapter-content-container">
        {/* Top Navigation - only shown if requested */}
        {showNavigation && (
          <Box mb={2} pt={1} pb={2} width="100%">
            <ChapterNavigation
              key={`top-nav-${language}-${currentChapterIndex}`}
              currentChapterIndex={currentChapterIndex}
              totalContentItems={totalContentItems}
              isFlipping={isNavigating}
              handlePreviousChapter={handlePreviousChapter}
              handleNextChapter={handleNextChapter}
              onOpenTableOfContents={onOpenTableOfContents}
              t={t}
              variant={navigationVariant}
              nextButtonText={nextButtonText}
            />
          </Box>
        )}

        {/* Content Box */}
        <Box
          ref={ref}
          p={{ base: 4, md: 4 }}
          borderRadius="md"
          bg={
            colorMode === "light"
              ? "rgba(240, 240, 240, 0.6)"
              : "rgba(30, 35, 45, 0.8)"
          }
          boxShadow={
            colorMode === "light"
              ? "0px 2px 10px rgba(0,0,0,0.1)"
              : "0px 2px 10px rgba(0,0,0,0.3)"
          }
          mb={showBottomMargin ? 6 : 2}
          onClick={!isNavigating ? onClick : undefined}
          position="relative"
          cursor="default"
          sx={{
            transition: "opacity 0.3s ease-out, background 0.2s ease",
            maxWidth: { base: "100%", md: "100%", lg: "95%", xl: "90%" },
            mx: "auto",
          }}
          _hover={{
            bg:
              colorMode === "light"
                ? "rgba(235, 235, 235, 0.8)"
                : "rgba(26, 32, 44, 0.8)",
          }}
        >
          {customContent ? (
            customContent
          ) : (
            <Box>
              {chapter && (
                <Heading
                  size="md"
                  mb={6}
                  textAlign={{ base: "center", md: "left" }}
                  fontSize={headingFontSize}
                  color={colorMode === "light" ? "gray.800" : "white"}
                >
                  {chapter}
                </Heading>
              )}
              {text && (
                <Text
                  fontSize={fontSize}
                  lineHeight="tall"
                  whiteSpace="pre-line"
                  color={colorMode === "light" ? "gray.800" : "gray.100"}
                >
                  {text}
                </Text>
              )}
              {children}

              {/* Navigation hint for mobile */}
              {adaptiveMobile && (
                <Flex justify="center" mt={16} mb={6}>
                  <Box
                    bg={
                      colorMode === "light"
                        ? "rgba(255, 255, 255, 0.75)"
                        : "rgba(30, 30, 30, 0.75)"
                    }
                    px={4}
                    py={2}
                    borderRadius="full"
                    boxShadow="md"
                    backdropFilter="blur(8px)"
                    sx={{ WebkitBackdropFilter: "blur(8px)" }}
                  >
                    <Text
                      fontSize="sm"
                      color={colorMode === "light" ? "gray.600" : "gray.300"}
                      fontStyle="italic"
                      textAlign="center"
                    >
                      {getNavigationHint()}
                    </Text>
                  </Box>
                </Flex>
              )}
            </Box>
          )}
        </Box>

        {/* Bottom Navigation - only shown if requested */}
        {showNavigation && (
          <Box mb={4} pt={2} pb={2} width="100%">
            <ChapterNavigation
              key={`bottom-nav-${language}-${currentChapterIndex}`}
              currentChapterIndex={currentChapterIndex}
              totalContentItems={totalContentItems}
              isFlipping={isNavigating}
              handlePreviousChapter={handlePreviousChapter}
              handleNextChapter={handleNextChapter}
              onOpenTableOfContents={onOpenTableOfContents}
              t={t}
              variant={navigationVariant}
              nextButtonText={nextButtonText}
            />
          </Box>
        )}
      </Box>
    );
  }
);

BookContent.displayName = "BookContent";

export default BookContent;
